import { BrainCircuit, RefreshCw } from "lucide-react";
import { Fragment, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { channelLabel } from "../costs/fundingSource";
import {
  defaultLlmModelsLoader,
  useLlmModels,
  type LlmConfigState,
  type LlmModelsLoader,
} from "./llmConfigClient";
import {
  STATUS_LABELS,
  describeModel,
  environmentLabel,
  formatInstant,
  formatModelUsd,
  groupAssignments,
  providerLine,
  type LlmConfigView,
  type ModelRef,
  type ObservedModel,
  type OverrideRow,
  type ReferenceRow,
  type StepRow,
  type StepStatus,
} from "./llmConfigModel";

// The "LLM Config" page: which AI model does which step, read live. Configured
// comes from the environment's signed runtime config, "running now" from the
// cost-ledger rows of the newest Echoes. Read-only: models are changed by
// publishing a runtime-config release, never from here. The only static table
// behind it is the step registry (labels and phases; no model names).

const NONE = "—";
const ALSO_SEEN_LIMIT = 4;

const STATUS_CLASS: Record<StepStatus, string> = {
  as_configured:
    "border-emerald-600/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  primary_and_fallback:
    "border-amber-600/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  fallback:
    "border-amber-600/40 bg-amber-500/10 text-amber-800 dark:text-amber-300",
  different:
    "border-rose-600/40 bg-rose-500/10 text-rose-700 dark:text-rose-300",
  not_seen: "text-muted-foreground",
  code_default: "border-transparent bg-secondary text-secondary-foreground",
  off: "text-muted-foreground",
  not_linked: "text-muted-foreground",
  ledger_unavailable: "text-muted-foreground",
  config_unavailable: "text-muted-foreground",
};

/** Status order for the header summary: what needs a look comes first. */
const SUMMARY_ORDER: StepStatus[] = [
  "different",
  "fallback",
  "primary_and_fallback",
  "as_configured",
  "code_default",
  "not_seen",
  "not_linked",
  "off",
  "ledger_unavailable",
  "config_unavailable",
];

const cell = "whitespace-normal align-top";

function StatusBadge({ status }: { status: StepStatus }) {
  return (
    <Badge variant="outline" className={STATUS_CLASS[status]}>
      {STATUS_LABELS[status]}
    </Badge>
  );
}

function ModelName({ model }: { model: string }) {
  return <span className="break-all font-mono text-xs">{model}</span>;
}

function FallbackChips({ fallbacks }: { fallbacks: ModelRef[] }) {
  if (!fallbacks.length) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        then
      </span>
      {fallbacks.map((fallback, index) => (
        <Badge
          key={`${describeModel(fallback)}:${index}`}
          variant="outline"
          className="whitespace-normal break-all font-mono text-[10px] font-normal"
          title={`Fallback ${index + 1}: ${describeModel(fallback)}`}
        >
          {[fallback.model, fallback.effort].filter(Boolean).join(" · ")}
        </Badge>
      ))}
    </div>
  );
}

function ConfiguredCell({ row }: { row: StepRow }) {
  if (!row.assignments.length) {
    return (
      <span className="text-xs text-muted-foreground">
        {row.status === "config_unavailable"
          ? "Unknown: the runtime config could not be read"
          : "No config key: the model is a default in the code"}
      </span>
    );
  }
  return (
    <div className="space-y-2">
      {groupAssignments(row.assignments).map((group) => (
        <div key={group.sources.join("|")}>
          <div className="flex flex-wrap items-center gap-1.5">
            <ModelName model={group.primary.model} />
            {group.inactive ? (
              <Badge variant="outline" className="text-[10px]">
                off
              </Badge>
            ) : null}
          </div>
          <div className="text-xs text-muted-foreground">
            {[group.primary.provider, group.primary.effort]
              .filter(Boolean)
              .join(" · ") || "provider not named in the config"}
          </div>
          <FallbackChips fallbacks={group.fallbacks} />
          <div
            className="mt-0.5 break-all text-[11px] text-muted-foreground"
            title={group.sources.join("\n")}
          >
            {group.sources[0]}
            {group.sources.length > 1
              ? ` (+${group.sources.length - 1} more with the same models)`
              : ""}
          </div>
        </div>
      ))}
    </div>
  );
}

function usageLine(model: ObservedModel): string {
  const parts = [
    `${model.calls.toLocaleString("en-US")} ${model.calls === 1 ? "call" : "calls"}`,
    `${model.sessions} ${model.sessions === 1 ? "Echo" : "Echoes"}`,
    formatModelUsd(model.usd),
  ];
  if (model.unpricedCalls > 0) parts.push(`${model.unpricedCalls} unpriced`);
  return parts.join(" · ");
}

function RunningNowCell({ row }: { row: StepRow }) {
  const current = row.current;
  if (!current) {
    return (
      <span className="text-xs text-muted-foreground">
        {row.status === "ledger_unavailable"
          ? "Unknown: the cost ledger could not be read"
          : "Not seen in the window"}
      </span>
    );
  }
  return (
    <div>
      <ModelName model={current.model} />
      <div className="text-xs text-muted-foreground">
        {providerLine(current.provider, current.channels.map(channelLabel))}
      </div>
      <div className="text-xs tabular-nums">{usageLine(current)}</div>
      <div className="text-[11px] text-muted-foreground">
        last seen {formatInstant(current.lastSeen)}
      </div>
    </div>
  );
}

function AlsoSeenCell({ models }: { models: ObservedModel[] }) {
  if (!models.length)
    return <span className="text-xs text-muted-foreground">{NONE}</span>;
  const shown = models.slice(0, ALSO_SEEN_LIMIT);
  const hidden = models.slice(ALSO_SEEN_LIMIT);
  return (
    <ul className="space-y-1">
      {shown.map((model) => (
        <li key={`${model.provider}:${model.model}`}>
          <ModelName model={model.model} />
          <div className="text-[11px] text-muted-foreground">
            last seen {formatInstant(model.lastSeen)} ·{" "}
            {model.calls.toLocaleString("en-US")}{" "}
            {model.calls === 1 ? "call" : "calls"}
          </div>
        </li>
      ))}
      {hidden.length ? (
        <li
          className="text-[11px] text-muted-foreground"
          title={hidden.map((model) => model.model).join("\n")}
        >
          +{hidden.length} more
        </li>
      ) : null}
    </ul>
  );
}

function WhoDoesWhat({ view }: { view: LlmConfigView }) {
  return (
    <Card data-testid="llm-config-steps">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Who does what</CardTitle>
        <p className="text-xs text-muted-foreground">
          One row per pipeline step. &ldquo;Configured&rdquo; is what an Echo
          starting now gets from the signed runtime config.{" "}
          {view.observed
            ? `"Running now" is the model with the newest ledger row among the ${view.observed.sessions_read} newest Echoes of the last ${view.observed.window.hours} hours.`
            : `"Running now" is empty: the cost ledger could not be read.`}
        </p>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[16%]">Step</TableHead>
              <TableHead className="w-[28%]">Configured</TableHead>
              <TableHead className="w-[22%]">Running now</TableHead>
              <TableHead className="w-[20%]">Also seen in window</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {view.phases.map((phase) => (
              <Fragment key={phase.id}>
                <TableRow
                  className="bg-muted/60 hover:bg-muted/60"
                  data-testid={`llm-phase-${phase.id}`}
                >
                  <TableCell
                    colSpan={5}
                    className="py-1.5 text-xs font-semibold uppercase tracking-wide"
                  >
                    {phase.label}
                    <span className="ml-2 font-normal normal-case text-muted-foreground">
                      {phase.rows.length}{" "}
                      {phase.rows.length === 1 ? "step" : "steps"}
                    </span>
                  </TableCell>
                </TableRow>
                {phase.rows.map((row) => (
                  <TableRow key={row.id} data-testid={`llm-step-${row.id}`}>
                    <TableCell className={cell}>
                      <div className="text-sm font-medium">{row.label}</div>
                      <div className="break-all font-mono text-[11px] text-muted-foreground">
                        {row.id.startsWith("config:")
                          ? "no ledger step known"
                          : row.id}
                      </div>
                    </TableCell>
                    <TableCell className={cell}>
                      <ConfiguredCell row={row} />
                    </TableCell>
                    <TableCell className={cell}>
                      <RunningNowCell row={row} />
                    </TableCell>
                    <TableCell className={cell}>
                      <AlsoSeenCell models={row.alsoSeen} />
                    </TableCell>
                    <TableCell className={cell}>
                      <StatusBadge status={row.status} />
                      {row.detail ? (
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {row.detail}
                        </p>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function OverrideAudience({ rule }: { rule: OverrideRow }) {
  const lines = [...rule.audience];
  if (rule.percent != null) lines.push(`${rule.percent}% of Echoes`);
  if (!lines.length) lines.push("Everyone the config key applies to");
  return (
    <ul className="space-y-0.5 text-xs">
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

function OverrideChanges({ rule }: { rule: OverrideRow }) {
  if (!rule.changes.length) {
    return (
      <span className="text-xs text-muted-foreground">
        Same models as the base value ({rule.assignmentCount} model{" "}
        {rule.assignmentCount === 1 ? "reference" : "references"} in the rule)
        {rule.turnsPolicyOn
          ? ". The base policy is off: this rule turns it on for these Echoes."
          : ""}
      </span>
    );
  }
  return (
    <ul className="space-y-1.5">
      {rule.changes.map((change) => (
        <li key={change.path}>
          <div className="text-[11px] text-muted-foreground">
            {change.path || "(the value itself)"}
          </div>
          <ModelName model={change.primary.model} />
          <span className="text-xs text-muted-foreground">
            {[change.primary.provider, change.primary.effort]
              .filter(Boolean)
              .map((part) => ` · ${part}`)
              .join("")}
          </span>
          <FallbackChips fallbacks={change.fallbacks} />
        </li>
      ))}
    </ul>
  );
}

function OverridesCard({ overrides }: { overrides: OverrideRow[] }) {
  return (
    <Card data-testid="llm-config-overrides">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          Cohort and percentage overrides
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Rules in the same release that give some Echoes a different value for
          a config key that names models. Only counts are shown: who is targeted
          stays in the signed snapshot.
        </p>
      </CardHeader>
      <CardContent>
        {overrides.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[26%]">Rule</TableHead>
                <TableHead className="w-[24%]">Config key</TableHead>
                <TableHead className="w-[20%]">Applies to</TableHead>
                <TableHead>Models it changes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {overrides.map((rule) => (
                <TableRow
                  key={`${rule.ruleId}:${rule.configKey}`}
                  data-testid={`llm-override-${rule.ruleId}`}
                  className={rule.inactive ? "opacity-70" : undefined}
                >
                  <TableCell className={cell}>
                    <div className="break-all font-mono text-xs">
                      {rule.ruleId}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      <Badge variant="secondary" className="text-[10px]">
                        {rule.effect}
                      </Badge>
                      {rule.inactive ? (
                        <Badge
                          variant="outline"
                          className="text-[10px] text-muted-foreground"
                          title={rule.inactiveReason ?? undefined}
                        >
                          {rule.percent === 0 ? "0%: inactive" : "inactive"}
                        </Badge>
                      ) : null}
                      <span className="text-[11px] text-muted-foreground">
                        {[
                          rule.priority != null
                            ? `priority ${rule.priority}`
                            : null,
                          rule.variant ? `variant ${rule.variant}` : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className={cell}>
                    <div className="break-all font-mono text-xs">
                      {rule.configKey}
                    </div>
                    {rule.steps.length ? (
                      <div className="text-[11px] text-muted-foreground">
                        {rule.steps.join(", ")}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className={cell}>
                    <OverrideAudience rule={rule} />
                  </TableCell>
                  <TableCell className={cell}>
                    <OverrideChanges rule={rule} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-sm text-muted-foreground">
            No rule in this release overrides a model.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function ReferencesCard({ references }: { references: ReferenceRow[] }) {
  const inactive = references.filter((row) => row.inactive).length;
  return (
    <Card data-testid="llm-config-references">
      <CardContent className="pt-6">
        <details>
          <summary className="cursor-pointer text-sm font-medium">
            Every model reference in the live config ({references.length}
            {inactive ? `, ${inactive} in policies that are off` : ""})
          </summary>
          <p className="mt-2 text-xs text-muted-foreground">
            Found by shape in the signed snapshot: a new key or role shows up
            here the moment it is published, whether or not this page knows
            which step it feeds.
          </p>
          <Table className="mt-2">
            <TableHeader>
              <TableRow>
                <TableHead>Config key</TableHead>
                <TableHead>Path</TableHead>
                <TableHead>Primary</TableHead>
                <TableHead>Fallbacks</TableHead>
                <TableHead>Feeds</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {references.map((row) => (
                <TableRow
                  key={`${row.config_key}:${row.path}`}
                  className={row.inactive ? "text-muted-foreground" : undefined}
                >
                  <TableCell
                    className={cn(cell, "break-all font-mono text-xs")}
                  >
                    {row.config_key}
                    {row.inactive ? (
                      <Badge variant="outline" className="ml-1.5 text-[10px]">
                        off
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell
                    className={cn(cell, "break-all font-mono text-xs")}
                  >
                    {row.path || "(value)"}
                  </TableCell>
                  <TableCell
                    className={cn(cell, "break-all font-mono text-xs")}
                  >
                    {describeModel(row.primary)}
                  </TableCell>
                  <TableCell
                    className={cn(cell, "break-all font-mono text-xs")}
                  >
                    {row.fallbacks.length
                      ? row.fallbacks.map(describeModel).join(" → ")
                      : NONE}
                  </TableCell>
                  <TableCell className={cn(cell, "text-xs")}>
                    {row.steps.length
                      ? `${row.steps.join(", ")}${row.referenceOnly ? " (reference only)" : ""}`
                      : "not mapped to a step"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </details>
      </CardContent>
    </Card>
  );
}

function Caveats({ view }: { view: LlmConfigView }) {
  const missing = [
    view.release == null
      ? "The runtime-config half of this answer is missing: the configured column and the overrides are empty, and every step reads as unknown rather than as a code default."
      : null,
    view.observed == null
      ? "The cost-ledger half of this answer is missing: nothing can be said about what is running now."
      : null,
    view.observed?.truncated
      ? `The ledger read stopped early: ${view.observed.sessions_read} of ${view.observed.sessions_requested} Echoes were read.`
      : null,
  ].filter((line): line is string => line != null);
  return (
    <div
      className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
      data-testid="llm-config-caveats"
    >
      <p>
        An Echo freezes its configuration when it starts, so right after a
        publish &ldquo;Running now&rdquo; still shows the previous model for
        Echoes that were already in flight.
      </p>
      {missing.length || view.notes.length ? (
        <ul className="mt-2 list-disc pl-4">
          {missing.map((line) => (
            <li key={line}>{line}</li>
          ))}
          {view.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function ReleaseLine({ view }: { view: LlmConfigView }) {
  const release = view.release;
  return (
    <p className="text-xs text-muted-foreground" data-testid="llm-config-meta">
      {release ? (
        <>
          Release{" "}
          <span className="font-medium text-foreground">
            #{release.sequence ?? "?"}
          </span>{" "}
          <span className="font-mono" title={release.id ?? undefined}>
            {release.id ? release.id.slice(0, 8) : "no id"}
          </span>
          , published {formatInstant(release.created_at)}
        </>
      ) : (
        "No runtime-config release could be read"
      )}
      {" · "}generated {formatInstant(view.generatedAt)}
      {view.observed
        ? ` · observed: ${view.observed.sessions_read} newest Echoes, last ${view.observed.window.hours} h`
        : ""}
    </p>
  );
}

function StatusSummary({ view }: { view: LlmConfigView }) {
  const present = SUMMARY_ORDER.filter((status) => view.counts[status] > 0);
  if (!present.length) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      data-testid="llm-config-summary"
    >
      {present.map((status) => (
        <Badge
          key={status}
          variant="outline"
          className={cn("font-normal", STATUS_CLASS[status])}
        >
          {view.counts[status]} {STATUS_LABELS[status].toLowerCase()}
        </Badge>
      ))}
    </div>
  );
}

function stateMessage(
  state: Exclude<LlmConfigState, { status: "ready" }>,
): string {
  switch (state.status) {
    case "loading":
      return "Loading the live model configuration…";
    case "unavailable":
      return "LLM Config needs the AWS dashboard API, which this environment does not use.";
    case "missing_endpoint":
      return "This dashboard API does not serve /config/llm-models yet (it ships with the dashboard-api LLM-config change). The page fills in by itself once that is deployed.";
    case "error":
      return `Could not read the model configuration: ${state.message}.`;
  }
}

export function LlmConfigPage({
  loadModels,
}: {
  /** Test seam; defaults to the dashboard API where it is the data provider. */
  loadModels?: LlmModelsLoader | null;
} = {}) {
  const [loader] = useState<LlmModelsLoader | null>(() =>
    loadModels === undefined ? defaultLlmModelsLoader() : loadModels,
  );
  const { state, refresh, refreshing } = useLlmModels(loader);
  const view = state.status === "ready" ? state.view : null;

  return (
    <div className="space-y-4 p-6">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <BrainCircuit className="size-5 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">LLM Config</h1>
            {view ? (
              <Badge
                variant={
                  view.environment === "stg" ? "destructive" : "secondary"
                }
              >
                {environmentLabel(view.environment)}
              </Badge>
            ) : null}
            {view ? (
              <Badge
                variant="outline"
                title="Read from the environment's signed runtime-config snapshot and its cost ledger on every load"
              >
                Live from signed config
              </Badge>
            ) : null}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={loader == null || refreshing}
          >
            <RefreshCw
              aria-hidden
              className={cn("size-4", refreshing && "animate-spin")}
            />
            Refresh
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Which AI model does which step of an Echo. Nothing here is a
          maintained list of models: the configured side is read from the signed
          runtime config, the running side from the cost ledger of the newest
          Echoes. It refreshes every minute; models are changed by publishing a
          runtime-config release, not from this page.
        </p>
        {view ? <ReleaseLine view={view} /> : null}
        {view ? <StatusSummary view={view} /> : null}
        {state.status === "ready" && state.refreshError ? (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            The last refresh failed ({state.refreshError}); showing the answer
            generated {formatInstant(state.view.generatedAt)}.
          </p>
        ) : null}
      </div>

      {state.status === "ready" ? (
        <>
          <Caveats view={state.view} />
          <WhoDoesWhat view={state.view} />
          <OverridesCard overrides={state.view.overrides} />
          <ReferencesCard references={state.view.references} />
        </>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <p
              className="text-sm text-muted-foreground"
              role={state.status === "loading" ? "status" : undefined}
            >
              {stateMessage(state)}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

LlmConfigPage.path = "/llm-config";
